import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Search, Users, UserCheck, UserX, Clock, Briefcase, Wallet } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminUserService, getApiErrorMessage } from '@/services';
import type { AdminListUser, ListUsersParams, UserStats } from '@/types/admin';
import { cn } from '@/utils/cn';
import { adminShell } from '@/utils/adminTheme';
import { formatDateForDisplay } from '@/utils/dates';
import { notify } from '@/utils/notify';

function adminUserDetailPath(id: number) {
  return `/admin/users/${id}`;
}

function formatNaira(amount: number) {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 2,
  }).format(amount);
}

function StatusBadge({ status }: { status: AdminListUser['status'] }) {
  const styles = {
    active: 'bg-secondary/15 text-secondary',
    blocked: 'bg-red-500/15 text-red-500',
    pending: 'bg-amber-500/15 text-amber-600',
  };

  return (
    <span className={cn('inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize', styles[status])}>
      {status}
    </span>
  );
}

function TypeBadge({ userType }: { userType: AdminListUser['userType'] }) {
  return (
    <span className="inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold capitalize text-primary">
      {userType}
    </span>
  );
}

export function AdminUsersPage() {
  const { theme } = useAdminTheme();
  const shell = adminShell(theme);

  const [stats, setStats] = useState<UserStats | null>(null);
  const [users, setUsers] = useState<AdminListUser[]>([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 1 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ListUsersParams['status']>('all');
  const [userType, setUserType] = useState<ListUsersParams['userType']>('all');

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await adminUserService.getUserStats();
      setStats(res.data?.stats ?? null);
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load user stats'));
    } finally {
      setLoadingStats(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await adminUserService.listUsers({
        page: pagination.page,
        limit: pagination.limit,
        search: search || undefined,
        status,
        userType,
      });
      setUsers(res.data?.users ?? []);
      if (res.data?.pagination) {
        setPagination((prev) => ({ ...prev, ...res.data!.pagination }));
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, 'Failed to load users'));
    } finally {
      setLoadingUsers(false);
    }
  }, [pagination.page, pagination.limit, search, status, userType]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  }

  const statCards = [
    { label: 'Total Users', value: stats?.total, icon: Users, accent: 'text-primary' },
    { label: 'Active', value: stats?.active, icon: UserCheck, accent: 'text-secondary' },
    { label: 'Blocked', value: stats?.blocked, icon: UserX, accent: 'text-red-500' },
    { label: 'Pending', value: stats?.pending, icon: Clock, accent: 'text-amber-500' },
    { label: 'Agents', value: stats?.agents, icon: Briefcase, accent: 'text-primary' },
    {
      label: 'Wallet Volume',
      value: stats ? formatNaira(stats.totalWalletBalance) : undefined,
      icon: Wallet,
      accent: 'text-secondary',
      isText: true,
    },
  ];

  const filterSelectClass = cn(
    'rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2',
    shell.select,
  );

  const filterInputClass = cn(
    'w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 sm:max-w-xs',
    shell.input,
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-bold sm:text-2xl">Users</h1>
        <p className={cn('text-sm', shell.cardMuted)}>Manage platform users, filters, and account status</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
        {statCards.map(({ label, value, icon: Icon, accent, isText }) => (
          <Card key={label} className={cn('p-4 sm:p-5', shell.card)}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className={cn('text-xs font-medium', shell.cardMuted)}>{label}</p>
                <p className={cn('mt-2 text-xl font-bold sm:text-2xl', isText && 'text-base sm:text-lg')}>
                  {loadingStats ? '—' : isText ? value : (value ?? 0).toLocaleString()}
                </p>
              </div>
              <div className={cn('rounded-xl bg-primary/10 p-2.5', accent)}>
                <Icon className="h-5 w-5" strokeWidth={2} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className={cn('space-y-4 p-4 sm:p-5', shell.card)}>
        <form onSubmit={handleSearchSubmit} className="flex flex-col gap-3 lg:flex-row lg:items-end">
          <div className="flex-1">
            <label htmlFor="user-search" className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
              Search
            </label>
            <div className="relative">
              <Search className={cn('pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2', shell.cardMuted)} />
              <input
                id="user-search"
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Name, email, or phone"
                className={cn(filterInputClass, 'pl-9 sm:max-w-none')}
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:flex lg:items-end lg:gap-3">
            <div>
              <label htmlFor="status-filter" className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
                Status
              </label>
              <select
                id="status-filter"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value as ListUsersParams['status']);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className={filterSelectClass}
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="blocked">Blocked</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            <div>
              <label htmlFor="type-filter" className={cn('mb-1.5 block text-xs font-medium', shell.cardMuted)}>
                Type
              </label>
              <select
                id="type-filter"
                value={userType}
                onChange={(e) => {
                  setUserType(e.target.value as ListUsersParams['userType']);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className={filterSelectClass}
              >
                <option value="all">All types</option>
                <option value="subscriber">Subscriber</option>
                <option value="agent">Agent</option>
              </select>
            </div>

            <button
              type="submit"
              className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Search
            </button>
          </div>
        </form>
      </Card>

      {/* Table */}
      <Card className={cn('overflow-hidden p-0', shell.card)}>
        {loadingUsers ? (
          <div className="flex items-center justify-center py-16">
            <Loader label="Loading users" />
          </div>
        ) : users.length === 0 ? (
          <div className={cn('py-16 text-center text-sm', shell.empty)}>No users match your filters.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className={cn('text-xs uppercase tracking-wide', shell.tableHead)}>
                  <tr>
                    <th className="px-4 py-3 font-semibold sm:px-5">User</th>
                    <th className="hidden px-4 py-3 font-semibold md:table-cell">Contact</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="hidden px-4 py-3 font-semibold sm:table-cell">Wallet</th>
                    <th className="hidden px-4 py-3 font-semibold lg:table-cell">Joined</th>
                    <th className="px-4 py-3 font-semibold sm:px-5">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className={cn('border-t', shell.tableRow)}>
                      <td className="px-4 py-3 sm:px-5">
                        <p className={cn('font-semibold', shell.tableCell)}>
                          {user.firstName} {user.lastName}
                        </p>
                        <p className={cn('mt-0.5 text-xs md:hidden', shell.cardMuted)}>{user.phone}</p>
                      </td>
                      <td className={cn('hidden px-4 py-3 md:table-cell', shell.tableCell)}>
                        <p>{user.phone}</p>
                        <p className={cn('text-xs', shell.cardMuted)}>{user.email ?? '—'}</p>
                      </td>
                      <td className="px-4 py-3">
                        <TypeBadge userType={user.userType} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={user.status} />
                      </td>
                      <td className={cn('hidden px-4 py-3 font-medium sm:table-cell', shell.tableCell)}>
                        {formatNaira(user.walletBalance)}
                      </td>
                      <td className={cn('hidden px-4 py-3 lg:table-cell', shell.cardMuted)}>
                        {formatDateForDisplay(user.createdAt)}
                      </td>
                      <td className="px-4 py-3 sm:px-5">
                        <Link
                          to={adminUserDetailPath(user.id)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/20"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              className={cn(
                'flex flex-col items-center justify-between gap-3 border-t px-4 py-3 sm:flex-row sm:px-5',
                shell.tableWrap,
              )}
            >
              <p className={cn('text-xs sm:text-sm', shell.cardMuted)}>
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  className={cn(
                    'rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40',
                    shell.outlineBtn,
                  )}
                >
                  Previous
                </button>
                <span className={cn('px-2 text-sm', shell.cardMuted)}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  type="button"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  className={cn(
                    'rounded-lg border px-3 py-1.5 text-sm font-medium disabled:opacity-40',
                    shell.outlineBtn,
                  )}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
